/**
 * Palette mode.
 *
 * `data-mode="dark"` on <html> is the presentation palette; its absence is the
 * design-system palette. Two things want to set it and they must not fight:
 *
 *   - the route, which has a natural default (home and details are dark, the
 *     results screen matches the client's real internal software and is light);
 *   - the ◐ control, which the presenter uses to flip mid-demo.
 *
 * Precedence: an explicit user override wins for the rest of the session, and
 * the route default applies only until the first override. Storing the override
 * in sessionStorage rather than localStorage is deliberate — a demo should start
 * from a predictable state in a fresh tab.
 */

export type Mode = "dark" | "light";

const OVERRIDE_KEY = "bulgari.mode";

function readOverride(): Mode | null {
  try {
    const v = sessionStorage.getItem(OVERRIDE_KEY);
    return v === "dark" || v === "light" ? v : null;
  } catch {
    return null; // private mode / storage disabled
  }
}

function writeOverride(mode: Mode): void {
  try {
    sessionStorage.setItem(OVERRIDE_KEY, mode);
  } catch {
    /* non-fatal: the toggle still works for this page view */
  }
}

function apply(mode: Mode): void {
  const root = document.documentElement;
  if (mode === "dark") root.dataset.mode = "dark";
  else delete root.dataset.mode;
}

/** Current mode as the DOM actually has it. */
export function currentMode(): Mode {
  return document.documentElement.dataset.mode === "dark" ? "dark" : "light";
}

/** Apply a route's preferred mode. A user override, if any, takes precedence. */
export function applyRouteMode(preferred: Mode): void {
  apply(readOverride() ?? preferred);
}

/** Flip the palette and remember the choice for the session. */
export function toggleMode(): Mode {
  const next: Mode = currentMode() === "dark" ? "light" : "dark";
  writeOverride(next);
  apply(next);
  return next;
}
